import * as React from 'react';
import { observer } from 'mobx-react';

import { PageEnterDetailsViewModel } from './page-enter-details-view-model';
import { PayerOnboardingUserAction } from '../../payer-onboarding-user-actions';
import { SubmitButton } from '../../../WebGiving/components/submit-button/submit-button';
import { TextboxField } from '../../../WebGiving/components/form-fields';
import { FormControl } from '../../../WebGiving/components/form-control/form-control';
import { Form } from '../../../WebGiving/primitives/form';
import { Link } from '../../../WebGiving/primitives/link/link';
import { ErrorMessage } from '../../components/error-message/error-message';
import { ResetPageReason } from '../../payer-onboarding-generated';
import { PanelContentTransition, panelContentTransitionDuration } from '../../../WebGiving/components/panel-content-transition';
import { FadeTransition } from '../../../WebGiving/components/fade-transition';
import { FocusOnMount } from '../../../WebGiving/components/focus-on-mount-hoc';
import { getPageClasses } from '../../../WebGiving/pages/helpers/get-page-classes';
import { PanelHeading } from '../../../WebGiving/components/panel-heading';
import { PanelFooter } from '../../../WebGiving/components/panel-footer';
import { MobilePhoneControl } from '../../../WebGiving/components/mobile-phone-control/mobile-phone-control';
import { pageFooter } from '../../../WebGiving/main/webgiving-main.less';
import { ModalType } from '../../modals/modal-type';

import * as style from './page-enter-details.less';

@observer
export class PageEnterDetails extends React.Component<{
	vm: PageEnterDetailsViewModel,
	raiseAction: (action: PayerOnboardingUserAction) => void,
	transitionState: string,
}> {
	panelBodyNode: HTMLDivElement;

	componentDidMount() {
		// Fetch App Content Card when this page loads
		this.props.raiseAction(new PayerOnboardingUserAction.FetchAppContentCard());
	}


	render() {
		const { vm, transitionState } = this.props;
		const {
			firstName,
			lastName,
			mobileNumber,
			mobileCountry,
			emailAddress,
			isBlocked,
			pageShouldBeVisible,
			isLoading,
			error,
			shouldFocusOnMobileNumberField,
			isAnonymousSignUp,
		} = vm;
		const elementsShouldEnter = transitionState === 'entered';

		return (
			<div className={getPageClasses(pageShouldBeVisible, isLoading)} data-pp-at-target={pageShouldBeVisible ? `Enter Details page` : null}>
				<Form onSubmit={this.handleSubmit}>
					<PanelHeading vm={vm}>
						<FadeTransition in={elementsShouldEnter}>
							<div>
								<h1 className="panel-title">PERSONAL DETAILS</h1>
								<p className="panel-subtitle">{`${isAnonymousSignUp ? 'Enter' : 'Confirm'} your details to create an account.`}</p>
							</div>
						</FadeTransition>
					</PanelHeading>
					<div className="panel-body panel-body-form" ref={this.savePanelBodyNode}>
						<PanelContentTransition in={elementsShouldEnter} setContentHeight={this.setContentHeight} className="panel-body-content">
							<div>
								<ErrorMessage
									error={error}
									onResetPage={this.handleResetPage}
								/>
								<div className="row">
									<FormControl label="First name" validationError={vm.firstName.validationError} className="col-sm-6">
										{shouldFocusOnMobileNumberField
											? <TextboxField formField={firstName} acceptanceTestTargetId="First Name" />
											: <FocusOnMount delay={panelContentTransitionDuration}>
												<TextboxField formField={firstName} acceptanceTestTargetId="First Name" />
											</FocusOnMount>
										}
									</FormControl>
									<FormControl label="Last name" validationError={vm.lastName.validationError} className="col-sm-6">
										<TextboxField formField={lastName} acceptanceTestTargetId="Last Name" />
									</FormControl>
								</div>
								{isAnonymousSignUp && <FormControl label="Email address" validationError={vm.emailAddress.validationError}>
									<TextboxField formField={emailAddress} acceptanceTestTargetId="Email Address" type="email" />
								</FormControl>}
								<div className="row">
									<div className={`col-sm-12 ${style.mobileNumberField}`}>
										<FormControl label="Mobile number" validationError={mobileNumber.validationError}>
											<MobilePhoneControl
												numberField={mobileNumber}
												countryField={mobileCountry}
												acceptanceTestTargetId="Mobile number"
												focusOnMount={shouldFocusOnMobileNumberField}
												aria-required={true}
											/>
										</FormControl>
										<div className={style.needHelp}><Link acceptanceTestTargetId="Need help" onClick={this.showHelp} branded={false}>Need help?</Link></div>
									</div>
								</div>
							</div>
						</PanelContentTransition>
					</div>
					<PanelFooter className={pageFooter}>
						<SubmitButton acceptanceTestTargetId="Next" isLoading={vm.isLoading} isBlocked={isBlocked}>Next</SubmitButton>
					</PanelFooter>
				</Form>
			</div>
		);
	}

	setContentHeight = (offsetHeight: number) => {
		if (this.panelBodyNode) {
			this.panelBodyNode.style.minHeight = `${offsetHeight}px`;
		}
	}

	private showHelp = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		this.props.raiseAction(new PayerOnboardingUserAction.OpenModalDialog(ModalType.NeedHelp));
	}

	private savePanelBodyNode = (panelBodyNode: HTMLDivElement) => this.panelBodyNode = panelBodyNode;

	private handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		this.props.raiseAction(new PayerOnboardingUserAction.SubmitCurrentPage());
	}

	private handleResetPage = (reason?: ResetPageReason) => {
		this.props.raiseAction(new PayerOnboardingUserAction.ResetPageBecauseOfError(reason));
	}
}
